import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Alert } from 'react-bootstrap';
import ValueRangeBuilder from './ValueRangeBuilder';

export interface ValueRange {
  min?: number;  // Valor mínimo da faixa (opcional, -Infinity se não especificado)
  max?: number;  // Valor máximo da faixa (opcional, Infinity se não especificado)
  // Validação a ser aplicada quando o valor está nesta faixa
  validation: {
    type: 'min_date_days' | 'min_value' | 'max_value' | 'equals' | 'custom';
    value: number | string;  // Valor da validação (ex: 26 para min_date_days, ou valor para equals)
    message?: string;  // Mensagem de erro personalizada (opcional)
  };
  // Condições adicionais específicas desta faixa (ex: checkbox)
  conditions?: FieldCondition[];
}

export interface FieldCondition {
  field_id: number;  // ID do campo que será verificado
  operator: 'equals' | 'not_equals' | 'checked' | 'unchecked';  // Operador de comparação
  value?: any;  // Valor esperado (para equals/not_equals)
  // Quando a condição é satisfeita, esta validação será aplicada ao invés da faixa normal
  override_validation?: {
    type: 'min_date_days' | 'min_value' | 'max_value' | 'equals' | 'custom';
    value: number | string;
    message?: string;
  };
  // Limite de valor para aplicar o override (ex: só aplicar se valor <= 30000)
  max_value_for_override?: number;
}

export interface ValidationRule {
  field_id: number;
  operator: 'equals' | 'greater_than' | 'greater_equal' | 'less_than' | 'less_equal' | 'contains' | 'not_equals' | 'min_date_days' | 'value_ranges';
  value: string | number;
  min_date_days?: number;
  value_range?: { min?: number; max?: number };
  // NOVOS CAMPOS:
  value_ranges?: ValueRange[];  // Múltiplas faixas personalizadas
  conditions?: FieldCondition[];  // Condições adicionais (ex: checkbox)
}

interface ValidationRuleBuilderProps {
  availableFields: Array<{ id: number; label: string; name: string; field_type: string; options?: string }>;
  validationFieldId?: number;
  rules: ValidationRule[];
  onChange: (rules: ValidationRule[]) => void;
}

const ValidationRuleBuilder: React.FC<ValidationRuleBuilderProps> = ({
  availableFields,
  validationFieldId,
  rules,
  onChange,
}) => {
  const [localRules, setLocalRules] = useState<ValidationRule[]>(rules || []);

  useEffect(() => {
    setLocalRules(rules || []);
  }, [rules]);

  const getOperatorsForFieldType = (fieldType: string): Array<{ value: string; label: string }> => {
    switch (fieldType) {
      case 'text_short':
      case 'text_long':
      case 'link':
        return [
          { value: 'equals', label: 'Igual a' },
          { value: 'not_equals', label: 'Diferente de' },
          { value: 'contains', label: 'Contém' },
        ];
      case 'integer':
      case 'decimal':
      case 'currency':
        return [
          { value: 'equals', label: 'Igual a' },
          { value: 'not_equals', label: 'Diferente de' },
          { value: 'greater_than', label: 'Maior que' },
          { value: 'greater_equal', label: 'Maior ou igual a' },
          { value: 'less_than', label: 'Menor que' },
          { value: 'less_equal', label: 'Menor ou igual a' },
          { value: 'value_ranges', label: 'Validação por Faixas' },
        ];
      case 'date':
        return [
          { value: 'equals', label: 'Igual a' },
          { value: 'greater_than', label: 'Depois de' },
          { value: 'greater_equal', label: 'Depois ou igual a' },
          { value: 'less_than', label: 'Antes de' },
          { value: 'less_equal', label: 'Antes ou igual a' },
          { value: 'min_date_days', label: 'Data mínima (dias a partir de hoje)' },
        ];
      case 'list':
      case 'combo':
        return [
          { value: 'equals', label: 'Igual a' },
          { value: 'not_equals', label: 'Diferente de' },
          { value: 'contains', label: 'Contém' },
        ];
      default:
        return [
          { value: 'equals', label: 'Igual a' },
          { value: 'not_equals', label: 'Diferente de' },
        ];
    }
  };

  const getSelectedField = (fieldId?: number) => {
    if (!fieldId) return null;
    return availableFields.find(f => f.id === fieldId);
  };

  const addRule = () => {
    if (!validationFieldId) return;
    
    const validationField = availableFields.find(f => f.id === validationFieldId);
    const operators = validationField ? getOperatorsForFieldType(validationField.field_type) : [];
    
    const newRule: ValidationRule = {
      field_id: validationFieldId, // Sempre usar o campo de validação selecionado
      operator: (operators[0]?.value as any) || 'equals',
      value: '',
    };
    
    const updatedRules = [...localRules, newRule];
    setLocalRules(updatedRules);
    onChange(updatedRules);
  };

  const removeRule = (index: number) => {
    const updatedRules = localRules.filter((_, i) => i !== index);
    setLocalRules(updatedRules);
    onChange(updatedRules);
  };

  const updateRule = (index: number, updates: Partial<ValidationRule>) => {
    const updatedRules = [...localRules];
    updatedRules[index] = { ...updatedRules[index], ...updates };
    setLocalRules(updatedRules);
    onChange(updatedRules);
  };

  const selectedField = getSelectedField(validationFieldId);
  const operators = selectedField ? getOperatorsForFieldType(selectedField.field_type) : [];

  // Se não há campo de validação selecionado, não mostrar nada
  if (!validationFieldId) {
    return null;
  }

  return (
    <div>
      {localRules.length === 0 && (
        <div className="mb-3">
          <Button variant="outline-primary" size="sm" onClick={addRule}>
            + Adicionar Regra de Validação
          </Button>
          {selectedField && ['integer', 'decimal', 'currency'].includes(selectedField.field_type) && (
            <Form.Text className="text-muted d-block mt-2">
              💡 Dica: Para campos numéricos, você pode usar "Validação por Faixas" para criar regras baseadas em intervalos de valores.
            </Form.Text>
          )}
        </div>
      )}

      {localRules.map((rule, index) => {
        // Usar o campo de validação selecionado como base
        const ruleField = getSelectedField(validationFieldId);
        const ruleOperators = ruleField ? getOperatorsForFieldType(ruleField.field_type) : [];

        return (
          <div key={index} className="mb-3 p-3 border rounded">
            <Row className="align-items-center g-2">
              <Col md={5}>
                <Form.Label className="small">Campo de Validação</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={ruleField ? `${ruleField.label} (${ruleField.name})` : ''}
                  disabled
                  className="bg-light"
                />
                <Form.Text className="text-muted small">
                  Campo usado como base para validação
                </Form.Text>
              </Col>

              <Col md={3}>
                <Form.Label className="small">Operador</Form.Label>
                <Form.Select
                  size="sm"
                  value={rule.operator}
                  onChange={(e) => {
                    const newOperator = e.target.value as any;
                    const updates: Partial<ValidationRule> = { 
                      operator: newOperator,
                      // Limpar valor se mudar para min_date_days ou value_ranges
                      value: (newOperator === 'min_date_days' || newOperator === 'value_ranges') ? '' : rule.value
                    };
                    // Se mudar para value_ranges, inicializar arrays vazios
                    if (newOperator === 'value_ranges') {
                      updates.value_ranges = [];
                      updates.conditions = [];
                    } else {
                      // Se mudar de value_ranges, limpar os arrays
                      if (rule.operator === 'value_ranges') {
                        updates.value_ranges = undefined;
                        updates.conditions = undefined;
                      }
                    }
                    updateRule(index, updates);
                  }}
                >
                  {ruleOperators.map((op) => (
                    <option key={op.value} value={op.value}>
                      {op.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              {rule.operator === 'value_ranges' ? (
                <Col md={12}>
                  <div className="mt-2">
                    <ValueRangeBuilder
                      ranges={rule.value_ranges || []}
                      onChange={(ranges) => updateRule(index, { value_ranges: ranges })}
                      availableFields={availableFields}
                      validationFieldId={validationFieldId}
                    />
                  </div>
                </Col>
              ) : (
                <>
                  <Col md={3}>
                    <Form.Label className="small">
                      {rule.operator === 'min_date_days' ? 'Dias Mínimos' : 'Valor'}
                    </Form.Label>
                    {(() => {
                      // Verificar primeiro o operador especial min_date_days
                      if (rule.operator === 'min_date_days') {
                        return (
                          <Form.Control
                            type="number"
                            size="sm"
                            min="0"
                            value={rule.min_date_days || ''}
                            onChange={(e) => updateRule(index, { min_date_days: parseInt(e.target.value) || 0 })}
                            placeholder="Ex: 7"
                          />
                        );
                      }
                      
                      // Verificar operadores numéricos
                      const numericOperators: Array<ValidationRule['operator']> = ['greater_than', 'greater_equal', 'less_than', 'less_equal'];
                      if (numericOperators.includes(rule.operator)) {
                        return (
                          <Form.Control
                            type="number"
                            size="sm"
                            step="0.01"
                            value={rule.value || ''}
                            onChange={(e) => updateRule(index, { value: e.target.value ? parseFloat(e.target.value) : '' })}
                            placeholder="Ex: 1000"
                          />
                        );
                      }
                      
                      // Verificar se é campo de data
                      if (ruleField?.field_type === 'date') {
                        const dateOperators: Array<ValidationRule['operator']> = ['equals', 'greater_than', 'greater_equal', 'less_than', 'less_equal'];
                        if (dateOperators.includes(rule.operator)) {
                          return (
                            <Form.Control
                              type="date"
                              size="sm"
                              value={rule.value as string || ''}
                              onChange={(e) => updateRule(index, { value: e.target.value })}
                            />
                          );
                        }
                      }
                      
                      // Verificar campos numéricos
                      if (['integer', 'decimal', 'currency'].includes(ruleField?.field_type || '')) {
                        return (
                          <Form.Control
                            type="number"
                            size="sm"
                            step={ruleField?.field_type === 'integer' ? '1' : '0.01'}
                            value={rule.value || ''}
                            onChange={(e) => updateRule(index, { value: e.target.value ? parseFloat(e.target.value) : '' })}
                            placeholder="Ex: 1000"
                          />
                        );
                      }
                      
                      // Verificar campos de lista/combo
                      if (ruleField?.field_type === 'list' || ruleField?.field_type === 'combo') {
                        return (
                          <Form.Select
                            size="sm"
                            value={rule.value as string || ''}
                            onChange={(e) => updateRule(index, { value: e.target.value })}
                          >
                            <option value="">Selecione...</option>
                            {ruleField?.options?.split(/[,\n]/).map((opt, idx) => (
                              <option key={idx} value={opt.trim()}>
                                {opt.trim()}
                              </option>
                            ))}
                          </Form.Select>
                        );
                      }
                      
                      // Padrão: input de texto
                      return (
                        <Form.Control
                          type="text"
                          size="sm"
                          value={rule.value as string || ''}
                          onChange={(e) => updateRule(index, { value: e.target.value })}
                          placeholder="Digite o valor"
                        />
                      );
                    })()}
                  </Col>

                  <Col md={1} className="d-flex align-items-end">
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => removeRule(index)}
                      className="w-100"
                    >
                      ×
                    </Button>
                  </Col>
                </>
              )}
            </Row>
          </div>
        );
      })}

      {localRules.length > 0 && (
        <Button variant="outline-primary" size="sm" onClick={addRule}>
          + Adicionar Outra Regra
        </Button>
      )}

      <Form.Text className="text-muted d-block mt-2">
        Todas as regras devem ser satisfeitas (AND). O campo será validado apenas se todas as condições forem verdadeiras.
      </Form.Text>
    </div>
  );
};

export default ValidationRuleBuilder;
